
import Vue, { PropType } from 'vue';
import Table from '@/components/common/Table.vue';
import { Alert } from '@/interfaces/alerts';
import { Device } from '@/interfaces/locations';
import { BvTableFieldArray } from 'bootstrap-vue/src/components/table';
import ConfirmActionModal from '@/components/modal/ConfirmActionModal.vue';
import dayjs from '@/plugins/dayjs';
import { Controller } from '@/interfaces/controllers';

/*
	All known backend alert type names. This section is picked up by vue-i18n. DO NOT REMOVE.

	// Alert type names
	this.$t('new_without_response');
	this.$t('connection_error');
	this.$t('light_control_error');
	this.$t('unreliable_rtc');
*/

interface AlertRow {
	id: number;
	device: string;
	alertType: string;
	description: string;
	alertTime: string;
	acknowledgment: boolean;
	acknowledgmentDisabled: boolean;
}

interface ComponentData {
	validMonth: boolean;
}

export default Vue.extend({
	components: {
		Table,
		ConfirmActionModal,
	},
	data(): ComponentData {
		return {
			validMonth: true,
		};
	},
	props: {
		alerts: {
			type: Array as PropType<Alert[]>,
			default: (): Alert[] => [],
		},
		devices: {
			type: Array as PropType<Device[]>,
			default: (): Device[] => [],
		},
		controllers: { type: Array as PropType<Controller[]>, default: (): Controller[] => [] },
		currentMonth: {
			type: String,
			default: dayjs().utc(true).startOf('month').format('YYYY-MM'),
		},
		showAlertHistory: { type: Boolean, default: false },
		loadingAlertHistory: { type: Boolean, default: false },
		userPermission: { type: Boolean, default: true },
	},
	computed: {
		fields(): BvTableFieldArray {
			return [
				{ key: 'device', label: this.$t('Device').toString(), sortable: true },
				{ key: 'alertType', label: this.$t('Type').toString(), sortable: true },
				{ key: 'description', label: this.$t('Description').toString(), sortable: true },
				{ key: 'alertTime', label: this.$t('First detected').toString(), sortable: true },
				{ key: 'lastDetected', label: this.$t('Last detected').toString(), sortable: true },
				{ key: 'acknowledgment', label: this.$t('Acknowledged').toString(), sortable: true },
			];
		},
		alertList(): AlertRow[] {
			return this.alerts.map((alert: Alert) => {
				const device: Device | undefined = this.devices.find(
					(device: Device) => device.id === alert.fk_device
				);
				const deviceGuid = this.controllers
					.find((controller) => controller.id === device?.fk_controller)
					?.guid.slice(-4);
				let errorDesc = '';
				if (['1', '2', '3', '256', '257', '258', '259', '512'].includes(alert.description)) {
					errorDesc =
						` - ` + this.$t(String(`light_control_error_` + alert.description)).toString();
				}
				return {
					id: alert.id,
					device: device ? (deviceGuid ? `${device.name} - ${deviceGuid}` : device.name) : '-',
					alertType: String(this.$t(alert.fk_alert_type)),
					description: `${alert.description}${errorDesc}`,
					alertTime: alert.dt_alert ? dayjs(alert.dt_alert).format('lll') : '-',
					lastDetected: alert.dt_created ? dayjs(alert.dt_created).format('lll') : '-',
					acknowledgment: alert.ack,
					acknowledgmentDisabled: this.showAlertHistory,
				};
			});
		},

		checkEveryAck(): boolean {
			if (this.showAlertHistory) {
				return false;
			}
			return this.alertList.every((alert: AlertRow) => alert.acknowledgment);
		},
	},
	methods: {
		changeAlertAck(row: AlertRow): void {
			this.$emit('changeAlertAck', row.id, { ack: row.acknowledgment });
		},
		closeConfirmModal(): void {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.alertListConfirmModal as any).hide();
		},
		confirmAckButton(): void {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.alertListConfirmModal as any).show();
		},
		changeEveryAck(): void {
			this.alertList
				.filter((alert: AlertRow) => !alert.acknowledgment)
				.forEach((alert) => this.$emit('changeAlertAck', alert.id, { ack: true }));
			this.closeConfirmModal();
		},
		changeAlertType(): void {
			this.$emit('changeAlertType');
		},
		changeMonth(value: string): void {
			if (dayjs(value, 'YYYY-MM', true).isValid()) {
				this.validMonth = true;
				this.$emit('changeMonth', value);
			} else {
				this.validMonth = false;
			}
		},
	},
});
